/**
 * LanguageSelector Component
 * 
 * A dropdown component for selecting the application language.
 * Integrates with the i18n system to clear caches when language changes.
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useLanguage, LANGUAGES, Language } from '../contexts/LanguageContext';
import { translationCache } from '../i18n';

interface LanguageSelectorProps {
  /** Show full language name instead of code */
  showName?: boolean;
  /** Compact mode (no text, just flag) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Dropdown position */
  position?: 'left' | 'right';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  showName = false,
  compact = false,
  className = '',
  position = 'right',
}) => {
  const { language, setLanguage, languageConfig } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleLanguageChange = async (lang: Language) => {
    if (lang === language) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);
    
    // Clear the translation cache for the old language
    // This ensures fresh translations are fetched for the new language
    try {
      translationCache.clear();
    } catch (e) {
      console.warn('Failed to clear translation cache:', e);
    }
    
    // Set the new language
    setLanguage(lang, window.location.pathname);
    setIsOpen(false);
    
    // Brief delay to show visual feedback
    setTimeout(() => {
      setIsChanging(false);
    }, 300);
  };

  const dropdownPositionClass = position === 'left' ? 'left-0' : 'right-0';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChanging}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg 
          hover:bg-slate-100 transition-[transform,box-shadow] will-change-transform
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1
          ${isChanging ? 'opacity-50 cursor-wait' : ''}
        `}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isChanging ? (
          <div className="w-5 h-5 border-2 border-slate-300 border-t-brand-600 rounded-full animate-spin" />
        ) : (
          <ReactCountryFlag
            countryCode={languageConfig.countryCode}
            svg
                cdnUrl="/flags/4x3/"
            style={{ width: '1.25em', height: '1.25em' }}
            aria-label={languageConfig.name}
          />
        )}
        
        {!compact && (
          <span className="hidden sm:inline text-sm font-medium text-slate-700">
            {showName ? languageConfig.nativeName : languageConfig.code.toUpperCase()}
          </span>
        )}
        
        <svg
          className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div 
          className={`
            absolute ${dropdownPositionClass} mt-2 w-48 
            bg-white rounded-lg shadow-lg border border-slate-200 
            py-1 z-50 
            animate-in fade-in slide-in-from-top-2 duration-200
          `}
          role="listbox"
          aria-label="Available languages"
        >
          {Object.values(LANGUAGES).map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              role="option"
              aria-selected={language === lang.code}
              className={`
                w-full flex items-center gap-3 px-4 py-2 text-left 
                hover:bg-slate-50 transition-colors
                focus:outline-none focus:bg-slate-50
                ${language === lang.code ? 'bg-brand-50 text-brand-700' : 'text-slate-700'}
              `}
            >
              <ReactCountryFlag
                countryCode={lang.countryCode}
                svg
                cdnUrl="/flags/4x3/"
                style={{ width: '1.5em', height: '1.5em' }}
                aria-hidden="true"
              />
              <div className="flex-1">
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs text-slate-500">{lang.name}</div>
              </div>
              {language === lang.code && (
                <svg 
                  className="w-5 h-5 text-brand-600" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;